import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from './useSubscription';
import { onTransactionChange } from './useTransactions';

export type ReportPeriod = '1m' | '2m' | '3m' | '6m' | '12m' | 'custom';

export interface ReportFiltersState {
  period: ReportPeriod;
  customStart: string;
  customEnd: string;
  tipo: 'todos' | 'receita' | 'despesa';
  origin: 'todos' | 'conta' | 'cartao';
  accountId: string;
  cardId: string;
  categoria: string;
  payeeId: string;
  status: 'todos' | 'pago' | 'pendente' | 'vencido';
  recorrente: 'todos' | 'sim' | 'nao';
  parcelado: 'todos' | 'sim' | 'nao';
}

export const DEFAULT_FILTERS: ReportFiltersState = {
  period: '1m',
  customStart: '',
  customEnd: '',
  tipo: 'todos',
  origin: 'todos',
  accountId: '',
  cardId: '',
  categoria: '',
  payeeId: '',
  status: 'todos',
  recorrente: 'todos',
  parcelado: 'todos',
};

export interface CategoryData {
  category: string;
  total: number;
  count: number;
  percent: number;
  color: string;
}

export interface MonthlyPoint {
  mes: string;
  mesLabel: string;
  year: number;
  month: number;
  receitas: number;
  despesas: number;
  saldo: number;
}

export interface AutoInsight {
  type: 'positive' | 'negative' | 'neutral' | 'warning';
  icon: string;
  text: string;
}

export interface PremiumInsight {
  type: 'positive' | 'negative' | 'neutral' | 'warning' | 'danger';
  icon: string;
  title: string;
  text: string;
  value?: string;
  priority: number;
}

export interface TransactionRow {
  id: string;
  date: string;
  description: string;
  category: string;
  type: 'receita' | 'despesa';
  origin: 'conta' | 'cartao';
  originName: string;
  status: string;
  amount: number;
  recurrence: string | null;
  installments: number;
  installment_number: number;
  payee_id: string | null;
  payee_name: string | null;
}

export interface AccountBreakdown {
  id: string;
  name: string;
  currentBalance: number;
  totalIncome: number;
  totalExpenses: number;
  transactionCount: number;
  pct: number;
}

export interface CardBreakdown {
  id: string;
  name: string;
  totalExpenses: number;
  transactionCount: number;
  pct: number;
  pendingAmount: number;
}

export interface CashFlowItem {
  id: string;
  date: string;
  description: string;
  category: string;
  type: 'receita' | 'despesa';
  amount: number;
  originName: string;
  recurrence: string | null;
  installments: number;
}

export interface CashFlowPeriod {
  income: number;
  expenses: number;
  net: number;
  items: CashFlowItem[];
}

export interface BalanceProjectionData {
  currentBalance: number;
  expectedIncome: number;
  pendingExpenses: number;
  overdueAmount: number;
  estimatedEndBalance: number;
  riskLevel: 'safe' | 'attention' | 'danger';
}

export interface CategoryGrowthItem {
  category: string;
  currentPeriod: number;
  prevPeriod: number;
  growth: number;
  color: string;
  isPending: number;
  isRecurrent: boolean;
}

export interface GoalVsReal {
  id: string;
  name: string;
  color: string;
  icon: string;
  targetAmount: number;
  currentAmount: number;
  progress: number;
  monthlyNeeded: number | null;
  isAtRisk: boolean;
  targetDate: string | null;
}

export interface ReportData {
  totalReceitas: number;
  totalDespesas: number;
  saldo: number;
  transactionCount: number;
  ticketMedio: number;
  totalPendente: number;
  pctRecorrentes: number;
  pctParcelados: number;
  topCategoria: string;
  distribConta: number;
  distribCartao: number;
  mediaDiaria: number;
  expensesByCategory: CategoryData[];
  incomeByCategory: CategoryData[];
  monthlyData: MonthlyPoint[];
  topExpenses: { description: string; amount: number; category: string; date: string }[];
  paidTotal: number;
  pendingTotal: number;
  overdueTotal: number;
  insights: AutoInsight[];
  premiumInsights: PremiumInsight[];
  prevPeriodDespesas: number;
  prevPeriodReceitas: number;
  filteredTransactions: TransactionRow[];
  categories: string[];
  balanceProjection: BalanceProjectionData;
  cashFlow30: CashFlowPeriod;
  cashFlow60: CashFlowPeriod;
  cashFlow90: CashFlowPeriod;
  accountBreakdown: AccountBreakdown[];
  cardBreakdown: CardBreakdown[];
  categoryGrowth: CategoryGrowthItem[];
  goalsVsReal: GoalVsReal[];
  payeeBreakdown: PayeeBreakdownItem[];
}

export interface AccountInfo { id: string; name: string; }
export interface CardInfo    { id: string; name: string; }

export interface PayeeBreakdownItem {
  payee_id: string | null;
  payee_name: string;
  total: number;
  count: number;
  percent: number;
  avgPerMonth: number;
  monthlyData: { month: string; monthLabel: string; total: number }[];
}

const CATEGORY_COLORS = [
  '#10B981','#F59E0B','#EF4444','#8B5CF6','#06B6D4',
  '#F97316','#EC4899','#14B8A6','#6366F1','#84CC16',
];

function getPeriodRange(filters: ReportFiltersState): { start: string; end: string; months: number } {
  if (filters.period === 'custom' && filters.customStart && filters.customEnd) {
    const diffMs = new Date(filters.customEnd).getTime() - new Date(filters.customStart).getTime();
    const months = Math.max(1, Math.round(diffMs / (30 * 24 * 60 * 60 * 1000)));
    return { start: filters.customStart, end: filters.customEnd, months };
  }
  const now = new Date();
  const end = now.toISOString().split('T')[0];
  const start = new Date(now);
  const monthMap: Record<string, number> = { '1m':1,'2m':2,'3m':3,'6m':6,'12m':12 };
  const months = monthMap[filters.period] ?? 1;
  start.setMonth(start.getMonth() - months);
  return { start: start.toISOString().split('T')[0], end, months };
}

function getPrevPeriodRange(start: string, months: number): { start: string; end: string } {
  const prevEnd = new Date(start);
  prevEnd.setDate(prevEnd.getDate() - 1);
  const prevStart = new Date(prevEnd);
  prevStart.setMonth(prevStart.getMonth() - months);
  return { start: prevStart.toISOString().split('T')[0], end: prevEnd.toISOString().split('T')[0] };
}

function buildMonthlyData(transactions: any[], months: number): MonthlyPoint[] {
  const result: MonthlyPoint[] = [];
  const now = new Date();
  for (let i = months - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const year = d.getFullYear();
    const month = d.getMonth() + 1;
    const mes = d.toLocaleDateString('pt-BR', { month: 'short' });
    const mesLabel = d.toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' });
    const monthTx = transactions.filter(t => {
      const dt = new Date(t.date || t.transaction_date);
      return dt.getFullYear() === year && dt.getMonth() + 1 === month;
    });
    const receitas = monthTx.filter(t => t.type === 'receita').reduce((s: number, t: any) => s + Number(t.amount), 0);
    const despesas = monthTx.filter(t => t.type === 'despesa').reduce((s: number, t: any) => s + Number(t.amount), 0);
    result.push({ mes, mesLabel, year, month, receitas, despesas, saldo: receitas - despesas });
  }
  return result;
}

function buildCategoryData(transactions: any[], type: 'receita' | 'despesa'): CategoryData[] {
  const filtered = transactions.filter(t => t.type === type);
  const total = filtered.reduce((s: number, t: any) => s + Number(t.amount), 0);
  const map: Record<string, { total: number; count: number }> = {};
  filtered.forEach((t: any) => {
    const cat = t.category || 'Outros';
    if (!map[cat]) map[cat] = { total: 0, count: 0 };
    map[cat].total += Number(t.amount);
    map[cat].count += 1;
  });
  return Object.entries(map)
    .sort((a, b) => b[1].total - a[1].total)
    .slice(0, 8)
    .map(([category, data], idx) => ({
      category, total: data.total, count: data.count,
      percent: total > 0 ? Math.round((data.total / total) * 100) : 0,
      color: CATEGORY_COLORS[idx % CATEGORY_COLORS.length],
    }));
}

function buildCashFlowPeriod(
  futureTxs: any[],
  accsMap: Record<string, string>,
  cdsMap: Record<string, string>,
  days: number,
): CashFlowPeriod {
  const now = new Date();
  const cutoff = new Date(now.getTime() + days * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  const today = now.toISOString().split('T')[0];
  const items = futureTxs.filter(t => {
    const d = t.date || '';
    return d >= today && d <= cutoff;
  });
  const income = items.filter(t => t.type === 'receita').reduce((s: number, t: any) => s + Number(t.amount), 0);
  const expenses = items.filter(t => t.type === 'despesa').reduce((s: number, t: any) => s + Number(t.amount), 0);
  return {
    income,
    expenses,
    net: income - expenses,
    items: items.map(t => ({
      id: t.id,
      date: t.date || '',
      description: t.description || '—',
      category: t.category || 'Outros',
      type: t.type as 'receita' | 'despesa',
      amount: Number(t.amount),
      originName: t.card_id ? (cdsMap[t.card_id] ?? 'Cartão') : (accsMap[t.account_id] ?? 'Conta'),
      recurrence: t.recurrence && t.recurrence !== 'none' ? t.recurrence : null,
      installments: Number(t.installments) || 1,
    })),
  };
}

function buildCategoryGrowth(
  currentTxs: any[],
  prevTxs: any[],
): CategoryGrowthItem[] {
  const buildMap = (txs: any[]) => {
    const m: Record<string, number> = {};
    txs.filter(t => t.type === 'despesa').forEach(t => {
      const c = t.category || 'Outros';
      m[c] = (m[c] || 0) + Number(t.amount);
    });
    return m;
  };
  const curr = buildMap(currentTxs);
  const prev = buildMap(prevTxs);
  const allCats = [...new Set([...Object.keys(curr), ...Object.keys(prev)])];
  return allCats
    .map((cat, idx) => {
      const c = curr[cat] || 0;
      const p = prev[cat] || 0;
      const growth = p > 0 ? ((c - p) / p) * 100 : (c > 0 ? 100 : 0);
      const isPending = currentTxs
        .filter(t => (t.category || 'Outros') === cat && ['pendente','vencido','atrasado'].includes(t.status))
        .reduce((s: number, t: any) => s + Number(t.amount), 0);
      const isRecurrent = currentTxs.some(t => (t.category || 'Outros') === cat && t.recurrence && t.recurrence !== 'none');
      return { category: cat, currentPeriod: c, prevPeriod: p, growth, isPending, isRecurrent, color: CATEGORY_COLORS[idx % CATEGORY_COLORS.length] };
    })
    .filter(c => c.currentPeriod > 0)
    .sort((a, b) => b.currentPeriod - a.currentPeriod)
    .slice(0, 10);
}

function buildPremiumInsights(data: Partial<ReportData>, accounts: any[], futureItems: any[]): PremiumInsight[] {
  const insights: PremiumInsight[] = [];
  const fmt = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  const bp = data.balanceProjection;
  if (bp) {
    if (bp.riskLevel === 'danger') {
      insights.push({ type: 'danger', icon: 'ri-alarm-warning-line', title: 'Risco de saldo negativo', text: `Saldo estimado ao fim do mês: ${fmt(bp.estimatedEndBalance)}. Considere cortar gastos ou antecipar receitas.`, value: fmt(bp.estimatedEndBalance), priority: 1 });
    } else if (bp.riskLevel === 'attention') {
      insights.push({ type: 'warning', icon: 'ri-error-warning-line', title: 'Atenção ao saldo projetado', text: `Saldo estimado de ${fmt(bp.estimatedEndBalance)} ao fim do período — margem apertada.`, value: fmt(bp.estimatedEndBalance), priority: 2 });
    } else {
      insights.push({ type: 'positive', icon: 'ri-shield-check-line', title: 'Projeção saudável', text: `Saldo estimado de ${fmt(bp.estimatedEndBalance)} ao fim do mês. Boa margem financeira.`, value: fmt(bp.estimatedEndBalance), priority: 5 });
    }
    if (bp.overdueAmount > 0) {
      insights.push({ type: 'danger', icon: 'ri-time-line', title: 'Contas vencidas', text: `Você tem ${fmt(bp.overdueAmount)} em lançamentos vencidos. Regularize para evitar juros.`, value: fmt(bp.overdueAmount), priority: 1 });
    }
  }

  const cf30 = data.cashFlow30;
  if (cf30 && cf30.expenses > 0) {
    const ratio = cf30.income > 0 ? cf30.expenses / cf30.income : 0;
    if (ratio > 0.9) {
      insights.push({ type: 'warning', icon: 'ri-calendar-line', title: 'Próximos 30 dias intensos', text: `Saídas de ${fmt(cf30.expenses)} vs. entradas de ${fmt(cf30.income)} nos próximos 30 dias.`, priority: 3 });
    }
  }

  if (data.pctRecorrentes !== undefined && data.pctRecorrentes > 50) {
    insights.push({ type: 'warning', icon: 'ri-repeat-2-line', title: 'Alto comprometimento fixo', text: `${data.pctRecorrentes}% dos seus gastos são recorrentes — despesas fixas dominando o orçamento.`, value: `${data.pctRecorrentes}%`, priority: 3 });
  }

  if (data.totalDespesas && data.prevPeriodDespesas && data.prevPeriodDespesas > 0) {
    const growthPct = Math.round(((data.totalDespesas - data.prevPeriodDespesas) / data.prevPeriodDespesas) * 100);
    if (growthPct > 20) {
      insights.push({ type: 'negative', icon: 'ri-trending-up-line', title: `Gastos cresceram ${growthPct}%`, text: `Despesas aumentaram ${growthPct}% em relação ao período anterior (${fmt(data.prevPeriodDespesas)}).`, value: `+${growthPct}%`, priority: 2 });
    } else if (growthPct < -10) {
      insights.push({ type: 'positive', icon: 'ri-trending-down-line', title: `Gastos reduziram ${Math.abs(growthPct)}%`, text: `Ótimo! Despesas caíram ${Math.abs(growthPct)}% vs. período anterior.`, value: `${growthPct}%`, priority: 4 });
    }
  }

  const goals = data.goalsVsReal ?? [];
  const atRiskGoals = goals.filter(g => g.isAtRisk);
  if (atRiskGoals.length > 0) {
    insights.push({ type: 'warning', icon: 'ri-flag-line', title: `${atRiskGoals.length} meta(s) em risco`, text: `"${atRiskGoals[0].name}" e outras ${atRiskGoals.length - 1 > 0 ? `${atRiskGoals.length - 1} meta(s)` : ''} estão abaixo do ritmo necessário.`, priority: 2 });
  }

  if (data.distribCartao !== undefined && data.totalDespesas && data.totalDespesas > 0) {
    const cartaoPct = Math.round((data.distribCartao / data.totalDespesas) * 100);
    if (cartaoPct > 70) {
      insights.push({ type: 'warning', icon: 'ri-bank-card-line', title: `${cartaoPct}% no cartão`, text: `A maioria dos gastos está no cartão. Cuidado com a concentração na fatura.`, value: `${cartaoPct}%`, priority: 3 });
    }
  }

  const categoryGrowth = data.categoryGrowth ?? [];
  const fastestGrowing = categoryGrowth.filter(c => c.growth > 50 && c.prevPeriod > 0).sort((a, b) => b.growth - a.growth)[0];
  if (fastestGrowing) {
    insights.push({ type: 'warning', icon: 'ri-arrow-up-circle-line', title: `"${fastestGrowing.category}" cresceu ${Math.round(fastestGrowing.growth)}%`, text: `Categoria com maior crescimento: de ${fmt(fastestGrowing.prevPeriod)} para ${fmt(fastestGrowing.currentPeriod)}.`, priority: 3 });
  }

  if (data.totalReceitas && data.totalDespesas && data.totalReceitas > 0) {
    const savingsRate = Math.round(((data.totalReceitas - data.totalDespesas) / data.totalReceitas) * 100);
    if (savingsRate >= 20) {
      insights.push({ type: 'positive', icon: 'ri-money-dollar-circle-line', title: `Taxa de poupança: ${savingsRate}%`, text: `Excelente! Você está poupando ${savingsRate}% das suas receitas — acima dos 20% recomendados.`, value: `${savingsRate}%`, priority: 5 });
    }
  }

  return insights.sort((a, b) => a.priority - b.priority).slice(0, 8);
}

function generateInsights(data: Omit<ReportData, 'insights' | 'premiumInsights'>, _filters: ReportFiltersState): AutoInsight[] {
  const insights: AutoInsight[] = [];
  const { totalReceitas, totalDespesas, saldo, prevPeriodDespesas, prevPeriodReceitas, expensesByCategory, transactionCount, ticketMedio, mediaDiaria, pctRecorrentes, totalPendente, distribConta, distribCartao } = data;
  const fmt = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  if (transactionCount === 0) {
    return [{ type: 'neutral', icon: 'ri-add-circle-line', text: 'Nenhum lançamento no período. Registre movimentações para receber insights.' }];
  }

  if (saldo > 0) insights.push({ type: 'positive', icon: 'ri-check-double-line', text: `Saldo positivo de ${fmt(saldo)} no período.` });
  else if (saldo < 0) insights.push({ type: 'negative', icon: 'ri-alert-line', text: `Despesas superaram receitas em ${fmt(Math.abs(saldo))}.` });

  if (prevPeriodDespesas > 0) {
    const diff = totalDespesas - prevPeriodDespesas;
    const pct = Math.abs(Math.round((diff / prevPeriodDespesas) * 100));
    if (diff > 5) insights.push({ type: 'warning', icon: 'ri-arrow-up-line', text: `Gastos ${pct}% maiores vs. período anterior (${fmt(prevPeriodDespesas)}).` });
    else if (diff < -5) insights.push({ type: 'positive', icon: 'ri-arrow-down-line', text: `Ótimo! Gastos ${pct}% menores vs. período anterior.` });
  }

  if (expensesByCategory.length > 0) {
    const top = expensesByCategory[0];
    insights.push({ type: 'neutral', icon: 'ri-pie-chart-line', text: `Maior gasto: "${top.category}" com ${top.percent}% do total (${fmt(top.total)}).` });
  }

  if (totalReceitas > 0) {
    const rate = Math.round((saldo / totalReceitas) * 100);
    if (rate >= 20) insights.push({ type: 'positive', icon: 'ri-money-dollar-circle-line', text: `Taxa de poupança de ${rate}% — acima dos 20% recomendados!` });
  }

  if (mediaDiaria > 0) insights.push({ type: 'neutral', icon: 'ri-calendar-line', text: `Média diária de gastos: ${fmt(mediaDiaria)} por dia.` });
  if (pctRecorrentes > 40) insights.push({ type: 'warning', icon: 'ri-repeat-line', text: `${pctRecorrentes}% dos gastos são recorrentes — despesas fixas elevadas.` });
  if (totalPendente > 0) insights.push({ type: 'warning', icon: 'ri-time-line', text: `${fmt(totalPendente)} em pagamentos pendentes/vencidos.` });
  if (ticketMedio > 0) insights.push({ type: 'neutral', icon: 'ri-receipt-line', text: `Ticket médio de despesas: ${fmt(ticketMedio)} por transação.` });

  const total = distribConta + distribCartao;
  if (total > 0 && distribCartao / total > 0.6) {
    insights.push({ type: 'warning', icon: 'ri-bank-card-line', text: `${Math.round((distribCartao/total)*100)}% dos gastos são no cartão.` });
  }

  return insights.slice(0, 7);
}

const EMPTY_CASH_FLOW: CashFlowPeriod = { income: 0, expenses: 0, net: 0, items: [] };
const EMPTY_PROJECTION: BalanceProjectionData = { currentBalance: 0, expectedIncome: 0, pendingExpenses: 0, overdueAmount: 0, estimatedEndBalance: 0, riskLevel: 'safe' };

function buildPayeeBreakdown(realTxs: any[], months: number): PayeeBreakdownItem[] {
  const expenses = realTxs.filter(t => t.type === 'despesa' && t.payee_name);
  if (expenses.length === 0) return [];

  const totalAll = expenses.reduce((s: number, t: any) => s + Number(t.amount), 0);

  const map: Record<string, {
    payee_id: string | null;
    payee_name: string;
    total: number;
    count: number;
    monthTotals: Record<string, number>;
  }> = {};

  expenses.forEach((t: any) => {
    const key = t.payee_name as string;
    if (!map[key]) map[key] = { payee_id: t.payee_id ?? null, payee_name: key, total: 0, count: 0, monthTotals: {} };
    map[key].total += Number(t.amount);
    map[key].count += 1;
    const month = (t.date || '').slice(0, 7);
    if (month) map[key].monthTotals[month] = (map[key].monthTotals[month] || 0) + Number(t.amount);
  });

  const now = new Date();
  const monthKeys: { key: string; label: string }[] = [];
  for (let i = Math.min(months, 6) - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' });
    monthKeys.push({ key, label });
  }

  return Object.values(map)
    .sort((a, b) => b.total - a.total)
    .slice(0, 15)
    .map(item => ({
      payee_id: item.payee_id,
      payee_name: item.payee_name,
      total: item.total,
      count: item.count,
      percent: totalAll > 0 ? Math.round((item.total / totalAll) * 100) : 0,
      avgPerMonth: months > 0 ? item.total / months : 0,
      monthlyData: monthKeys.map(mk => ({
        month: mk.key,
        monthLabel: mk.label,
        total: item.monthTotals[mk.key] || 0,
      })),
    }));
}

export const useReports = (filters: ReportFiltersState = DEFAULT_FILTERS) => {
  const { user } = useAuth();
  // ── Source of truth for plan status: subscriptions table via useSubscription ──
  // Never use profile.plan here — subscriptions is the authoritative source.
  const { isPaid } = useSubscription();
  const isPremium = isPaid;
  const [data, setData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState<AccountInfo[]>([]);
  const [cards, setCards] = useState<CardInfo[]>([]);

  const fetch = useCallback(async () => {
    if (!user) { setLoading(false); return; }
    setLoading(true);

    const { start, end, months } = getPeriodRange(filters);
    const prev = getPrevPeriodRange(start, months);
    const now = new Date();
    const today = now.toISOString().split('T')[0];
    const in90Days = new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

    const [curRes, prevRes, accsRes, cdsRes, futureRes, goalsRes] = await Promise.all([
      supabase.from('transactions').select('id,type,amount,category,description,date,status,recurrence,installments,installment_number,account_id,card_id,transaction_subtype,payee_id,payee_name').eq('user_id', user.id).gte('date', start).lte('date', end).is('deleted_at', null).order('date', { ascending: false }),
      supabase.from('transactions').select('type,amount,category,status,transaction_subtype').eq('user_id', user.id).gte('date', prev.start).lte('date', prev.end).is('deleted_at', null),
      supabase.from('accounts').select('id,name,current_balance,color').eq('user_id', user.id),
      supabase.from('cards').select('id,name').eq('user_id', user.id),
      supabase.from('transactions').select('id,type,amount,category,description,date,status,recurrence,installments,account_id,card_id,transaction_subtype').eq('user_id', user.id).gte('date', today).lte('date', in90Days).is('deleted_at', null).order('date', { ascending: true }),
      supabase.from('goals').select('id,name,color,icon,target_amount,current_amount,target_date,status,is_completed,created_at').eq('user_id', user.id).eq('is_completed', false),
    ]);

    const rawTxs = (curRes.data || []) as any[];
    const prevTxs = (prevRes.data || []) as any[];
    const futureTxs = (futureRes.data || []) as any[];
    const rawAccounts = (accsRes.data ?? []) as any[];
    const rawCards = (cdsRes.data ?? []) as any[];
    const rawGoals = (goalsRes.data ?? []) as any[];

    const accsMap: Record<string, string> = {};
    const cdsMap: Record<string, string> = {};
    rawAccounts.forEach((a: any) => { accsMap[a.id] = a.name; });
    rawCards.forEach((c: any) => { cdsMap[c.id] = c.name; });

    setAccounts(rawAccounts.map((a: any) => ({ id: a.id, name: a.name })));
    setCards(rawCards.map((c: any) => ({ id: c.id, name: c.name })));

    let txs = rawTxs.filter(t => {
      if (filters.tipo !== 'todos' && t.type !== filters.tipo) return false;
      if (filters.origin !== 'todos') {
        if (filters.origin === 'conta' && !t.account_id) return false;
        if (filters.origin === 'cartao' && !t.card_id) return false;
      }
      if (filters.accountId && t.account_id !== filters.accountId) return false;
      if (filters.cardId && t.card_id !== filters.cardId) return false;
      if (filters.categoria && t.category !== filters.categoria) return false;
      if (filters.payeeId && t.payee_id !== filters.payeeId) return false;
      if (filters.status !== 'todos' && t.status !== filters.status) return false;
      if (filters.recorrente === 'sim' && (!t.recurrence || t.recurrence === 'none')) return false;
      if (filters.recorrente === 'nao' && t.recurrence && t.recurrence !== 'none') return false;
      if (filters.parcelado === 'sim' && (!t.installments || t.installments <= 1)) return false;
      if (filters.parcelado === 'nao' && t.installments > 1) return false;
      return true;
    });

    const realTxs = txs.filter(t => t.transaction_subtype !== 'transferencia');

    const totalReceitas = realTxs.filter(t => t.type === 'receita').reduce((s: number, t: any) => s + Number(t.amount), 0);
    const totalDespesas = realTxs.filter(t => t.type === 'despesa').reduce((s: number, t: any) => s + Number(t.amount), 0);

    // REGRA: saldo = totalReceitas - totalDespesas com a mesma base de filtro.
    // Consistência garantida: a conta SEMPRE fecha independente do filtro de status.
    // Se o usuário filtra por 'pago'/'recebido': saldo usa só realizados.
    // Se filtra por 'todos': saldo usa todos (incluindo pendentes).
    // Nunca mais receita - despesa ≠ saldo.
    const saldo = totalReceitas - totalDespesas;

    const prevPeriodReceitas = prevTxs.filter(t => t.type === 'receita' && t.transaction_subtype !== 'transferencia').reduce((s: number, t: any) => s + Number(t.amount), 0);
    const prevPeriodDespesas = prevTxs.filter(t => t.type === 'despesa' && t.transaction_subtype !== 'transferencia').reduce((s: number, t: any) => s + Number(t.amount), 0);

    const expenses = realTxs.filter(t => t.type === 'despesa');
    const ticketMedio = expenses.length > 0 ? totalDespesas / expenses.length : 0;
    const totalPendente = realTxs.filter(t => ['pendente','vencido','atrasado'].includes(t.status)).reduce((s: number, t: any) => s + Number(t.amount), 0);
    const paidTotal = realTxs.filter(t => t.status === 'pago' || t.status === 'recebido').reduce((s: number, t: any) => s + Number(t.amount), 0);
    const pendingTotal = realTxs.filter(t => t.status === 'pendente').reduce((s: number, t: any) => s + Number(t.amount), 0);
    const overdueTotal = realTxs.filter(t => ['vencido','atrasado'].includes(t.status)).reduce((s: number, t: any) => s + Number(t.amount), 0);
    const recorrentes = expenses.filter(t => t.recurrence && t.recurrence !== 'none');
    const pctRecorrentes = expenses.length > 0 ? Math.round((recorrentes.length / expenses.length) * 100) : 0;
    const parcelados = expenses.filter(t => t.installments > 1);
    const pctParcelados = expenses.length > 0 ? Math.round((parcelados.length / expenses.length) * 100) : 0;
    const distribConta = realTxs.filter(t => t.account_id && !t.card_id && t.type === 'despesa').reduce((s: number, t: any) => s + Number(t.amount), 0);
    const distribCartao = realTxs.filter(t => t.card_id && t.type === 'despesa').reduce((s: number, t: any) => s + Number(t.amount), 0);
    const diffDays = Math.max(1, Math.round((new Date(end).getTime() - new Date(start).getTime()) / (1000 * 60 * 60 * 24)));
    const mediaDiaria = totalDespesas / diffDays;

    const expensesByCategory = buildCategoryData(realTxs, 'despesa');
    const incomeByCategory = buildCategoryData(realTxs, 'receita');
    const monthlyData = buildMonthlyData(
      rawTxs.filter(t => t.transaction_subtype !== 'transferencia'),
      months
    );
    const topCategoria = expensesByCategory[0]?.category ?? '—';
    const topExpenses = expenses.sort((a, b) => Number(b.amount) - Number(a.amount)).slice(0, 10).map(t => ({ description: t.description, amount: Number(t.amount), category: t.category || 'Outros', date: t.date || '' }));
    const categories = [...new Set(rawTxs.map((t: any) => t.category).filter(Boolean))].sort() as string[];

    const filteredTransactions: TransactionRow[] = txs.map(t => ({
      id: t.id, date: t.date || '', description: t.description || '—', category: t.category || 'Outros',
      type: t.type as 'receita' | 'despesa', origin: t.card_id ? 'cartao' : 'conta',
      originName: t.card_id ? (cdsMap[t.card_id] ?? 'Cartão') : (accsMap[t.account_id] ?? 'Conta'),
      status: t.status || '—', amount: Number(t.amount),
      recurrence: t.recurrence && t.recurrence !== 'none' ? t.recurrence : null,
      installments: Number(t.installments) || 1, installment_number: Number(t.installment_number) || 1,
      payee_id: t.payee_id ?? null,
      payee_name: t.payee_name ?? null,
    }));

    const accountBreakdown: AccountBreakdown[] = rawAccounts.map((acc: any) => {
      const accTxs = realTxs.filter(t => t.account_id === acc.id);
      const totalIncome = accTxs.filter(t => t.type === 'receita').reduce((s: number, t: any) => s + Number(t.amount), 0);
      const totalExpensesAcc = accTxs.filter(t => t.type === 'despesa').reduce((s: number, t: any) => s + Number(t.amount), 0);
      return { id: acc.id, name: acc.name, currentBalance: Number(acc.current_balance ?? 0), totalIncome, totalExpenses: totalExpensesAcc, transactionCount: accTxs.length, pct: totalDespesas > 0 ? Math.round((totalExpensesAcc / totalDespesas) * 100) : 0 };
    }).filter((a: AccountBreakdown) => a.transactionCount > 0).sort((a: AccountBreakdown, b: AccountBreakdown) => b.totalExpenses - a.totalExpenses);

    const cardBreakdown: CardBreakdown[] = rawCards.map((card: any) => {
      const cardTxs = realTxs.filter(t => t.card_id === card.id);
      const totalExpensesCard = cardTxs.reduce((s: number, t: any) => s + Number(t.amount), 0);
      const pendingAmount = cardTxs.filter(t => ['pendente','vencido','atrasado'].includes(t.status)).reduce((s: number, t: any) => s + Number(t.amount), 0);
      return { id: card.id, name: card.name, totalExpenses: totalExpensesCard, transactionCount: cardTxs.length, pct: totalDespesas > 0 ? Math.round((totalExpensesCard / totalDespesas) * 100) : 0, pendingAmount };
    }).filter((c: CardBreakdown) => c.transactionCount > 0).sort((a: CardBreakdown, b: CardBreakdown) => b.totalExpenses - a.totalExpenses);

    const categoryGrowth = buildCategoryGrowth(realTxs, prevTxs.filter(t => t.transaction_subtype !== 'transferencia'));

    const currentBalance = rawAccounts.reduce((s: number, a: any) => s + Number(a.current_balance ?? 0), 0);
    const monthEnd2 = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
    const realFutureTxs = futureTxs.filter(t => t.transaction_subtype !== 'transferencia');
    const monthFutureTxs = realFutureTxs.filter(t => t.date >= today && t.date <= monthEnd2);
    const expectedIncome = monthFutureTxs.filter(t => t.type === 'receita' && ['pendente', 'atrasado'].includes(t.status)).reduce((s: number, t: any) => s + Number(t.amount), 0);
    const pendingExpensesProj = monthFutureTxs.filter(t => t.type === 'despesa' && ['pendente','vencido','atrasado'].includes(t.status)).reduce((s: number, t: any) => s + Number(t.amount), 0);
    const overdueAmountProj = realFutureTxs.filter(t => t.status === 'vencido' || t.status === 'atrasado').reduce((s: number, t: any) => s + Number(t.amount), 0);
    const estimatedEndBalance = currentBalance + expectedIncome - pendingExpensesProj;
    const riskLevel: BalanceProjectionData['riskLevel'] = estimatedEndBalance < 0 ? 'danger' : estimatedEndBalance < currentBalance * 0.2 ? 'attention' : 'safe';
    const balanceProjection: BalanceProjectionData = { currentBalance, expectedIncome, pendingExpenses: pendingExpensesProj, overdueAmount: overdueAmountProj, estimatedEndBalance, riskLevel };

    const cashFlow30 = buildCashFlowPeriod(realFutureTxs, accsMap, cdsMap, 30);
    const cashFlow60 = buildCashFlowPeriod(realFutureTxs, accsMap, cdsMap, 60);
    const cashFlow90 = buildCashFlowPeriod(realFutureTxs, accsMap, cdsMap, 90);

    const goalsVsReal: GoalVsReal[] = rawGoals.map((g: any) => {
      const progress = g.target_amount > 0 ? Math.min(100, (g.current_amount / g.target_amount) * 100) : 0;
      let monthlyNeeded: number | null = null;
      let isAtRisk = false;
      if (g.target_date) {
        const target = new Date(g.target_date + 'T12:00:00');
        const mthsLeft = Math.max(0.5, (target.getFullYear() - now.getFullYear()) * 12 + (target.getMonth() - now.getMonth()));
        const remaining = Math.max(0, g.target_amount - g.current_amount);
        monthlyNeeded = remaining / mthsLeft;
        const totalMths = Math.max(1, (target.getFullYear() - new Date(g.created_at).getFullYear()) * 12 + (target.getMonth() - new Date(g.created_at).getMonth()));
        const elapsedMths = Math.max(0, (now.getFullYear() - new Date(g.created_at).getFullYear()) * 12 + (now.getMonth() - new Date(g.created_at).getMonth()));
        const expected = totalMths > 0 ? (elapsedMths / totalMths) * 100 : 0;
        isAtRisk = progress < expected * 0.65;
      }
      return { id: g.id, name: g.name, color: g.color || '#10B981', icon: g.icon || 'ri-flag-line', targetAmount: Number(g.target_amount), currentAmount: Number(g.current_amount), progress, monthlyNeeded, isAtRisk, targetDate: g.target_date };
    });

    const payeeBreakdown = buildPayeeBreakdown(realTxs, months);

    const partial = {
      totalReceitas, totalDespesas, saldo,
      transactionCount: realTxs.length, ticketMedio, totalPendente,
      pctRecorrentes, pctParcelados, topCategoria, distribConta, distribCartao, mediaDiaria,
      expensesByCategory, incomeByCategory, monthlyData, topExpenses, paidTotal, pendingTotal, overdueTotal,
      prevPeriodDespesas, prevPeriodReceitas, filteredTransactions, categories,
      balanceProjection, cashFlow30, cashFlow60, cashFlow90,
      accountBreakdown, cardBreakdown, categoryGrowth, goalsVsReal,
      payeeBreakdown,
    };

    setData({
      ...partial,
      insights: generateInsights(partial as any, filters),
      premiumInsights: buildPremiumInsights(partial as any, rawAccounts, futureTxs),
    });
    setLoading(false);
  }, [user, filters, isPaid]);

  useEffect(() => { fetch(); }, [fetch]);

  // ── Listen for transaction changes from any part of the app ───────────────
  // When transactions are created/edited/deleted anywhere, reports auto-refresh.
  useEffect(() => {
    return onTransactionChange(() => {
      fetch();
    });
  }, [fetch]);

  return { data, loading, isPremium, accounts, cards, refetch: fetch };
};
